import { useState, useEffect, useCallback } from "react";
import { StudioLayout } from "./StudioLayout";
import { JobBrowser } from "./JobBrowser";
import { ArtifactInspector } from "./ArtifactInspector";
import { SectionDetails } from "./SectionDetails";
import { SectionEditor } from "./SectionEditor";
import { StageProgress } from "./StageProgress";
import {
  getJobArtifacts,
  type StudioJobSummary,
  type StudioJobArtifacts,
  type SectionArtifact,
} from "../../client";

type ViewMode = "inspect" | "edit";

export function Studio() {
  const [selectedJob, setSelectedJob] = useState<StudioJobSummary | null>(null);
  const [artifacts, setArtifacts] = useState<StudioJobArtifacts | null>(null);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [artifactsError, setArtifactsError] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<SectionArtifact | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("inspect");

  const loadArtifacts = useCallback(async () => {
    if (!selectedJob) return;

    setLoadingArtifacts(true);
    setArtifactsError(null);
    try {
      const data = await getJobArtifacts(selectedJob.directory);
      setArtifacts(data);
    } catch (err) {
      setArtifactsError(err instanceof Error ? err.message : "Failed to load artifacts");
    } finally {
      setLoadingArtifacts(false);
    }
  }, [selectedJob]);

  useEffect(() => {
    if (!selectedJob) {
      setArtifacts(null);
      setSelectedSection(null);
      setViewMode("inspect");
      return;
    }

    setSelectedSection(null);
    loadArtifacts();
  }, [selectedJob?.directory, loadArtifacts]);

  const handleSelectJob = (job: StudioJobSummary) => {
    setSelectedJob(job);
  };

  const handleSelectSection = (section: SectionArtifact) => {
    setSelectedSection(section);
  };

  const handleEditSection = (section: SectionArtifact) => {
    setSelectedSection(section);
    setViewMode("edit");
  };

  const handleRenderComplete = () => {
    // Reload artifacts to get updated video status
    loadArtifacts();
  };

  return (
    <StudioLayout
      sidebar={
        <JobBrowser
          onSelectJob={handleSelectJob}
          selectedDirectory={selectedJob?.directory}
        />
      }
      main={
        viewMode === "edit" && selectedSection && artifacts ? (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Edit mode header */}
            <div
              style={{
                padding: "0.5rem 1rem",
                borderBottom: "1px solid #333",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#1a1a1a",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button
                  onClick={() => setViewMode("inspect")}
                  style={{
                    padding: "0.375rem 0.75rem",
                    backgroundColor: "#333",
                    border: "none",
                    borderRadius: "4px",
                    color: "#e0e0e0",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.375rem",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  Back to Inspector
                </button>
                <span style={{ color: "#888", fontSize: "0.75rem" }}>Editing Mode</span>
              </div>
            </div>
            {/* Editor */}
            <div style={{ flex: 1, minHeight: 0 }}>
              <SectionEditor
                section={selectedSection}
                directory={artifacts.directory}
                onRenderComplete={handleRenderComplete}
              />
            </div>
          </div>
        ) : (
          <MainContent
            selectedJob={selectedJob}
            artifacts={artifacts}
            loading={loadingArtifacts}
            error={artifactsError}
            onSelectSection={handleSelectSection}
            onEditSection={handleEditSection}
            selectedSectionId={selectedSection?.section_id}
          />
        )
      }
      details={
        viewMode === "inspect" && selectedSection && artifacts ? (
          <SectionDetails section={selectedSection} directory={artifacts.directory} />
        ) : undefined
      }
    />
  );
}

function MainContent({
  selectedJob,
  artifacts,
  loading,
  error,
  onSelectSection,
  onEditSection,
  selectedSectionId,
}: {
  selectedJob: StudioJobSummary | null;
  artifacts: StudioJobArtifacts | null;
  loading: boolean;
  error: string | null;
  onSelectSection: (section: SectionArtifact) => void;
  onEditSection: (section: SectionArtifact) => void;
  selectedSectionId?: string | null;
}) {
  if (!selectedJob) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#888",
        }}
      >
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
          <svg
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        </div>
        <h2 style={{ margin: "0 0 0.5rem 0", fontSize: "1.5rem", fontWeight: 600, color: "#e0e0e0" }}>
          Video Generation Studio
        </h2>
        <p style={{ margin: 0, fontSize: "1rem", textAlign: "center", maxWidth: "400px" }}>
          Select a job from the sidebar to inspect artifacts, view sections, and optimize your videos.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#888",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #333",
              borderTop: "3px solid #4a90d9",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 1rem",
            }}
          />
          <div>Loading artifacts...</div>
        </div>
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#f44336",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>Error</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (!artifacts) {
    return null;
  }

  const totalSections = artifacts.sections.length;
  const completedSections = artifacts.sections.filter((s) => s.status === "rendered").length;
  const sectionProgress = artifacts.sections.map((s) => ({
    sectionId: s.section_id,
    title: s.title,
    status:
      s.status === "rendered"
        ? ("complete" as const)
        : s.status === "failed"
        ? ("error" as const)
        : ("pending" as const),
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem", height: "100%" }}>
      {/* Section progress summary */}
      <div style={{ backgroundColor: "#0f1115", border: "1px solid #1f2430", borderRadius: "10px", padding: "0.85rem" }}>
        <StageProgress
          currentStage="render"
          sections={sectionProgress}
          completedSections={completedSections}
          totalSections={totalSections}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <ArtifactInspector
          artifacts={artifacts}
          onSelectSection={onSelectSection}
          onEditSection={onEditSection}
          selectedSectionId={selectedSectionId}
        />
      </div>
    </div>
  );
}

export default Studio;
